import { useEffect, useState, useCallback } from "react";
import { Building2, LayoutGrid, Star, RefreshCw, Plus, Sparkles } from "lucide-react";
import { useAdsSpyStore } from "../store/adsSpyStore";
import { adsSpyService } from "../services/adsSpy.service";
import AdsSpyFilters from "./AdsSpyFilters";
import AdsSpyGrid from "./AdsSpyGrid";
import BrandsTable from "./BrandsTable";
import BookmarksGrid from "./BookmarksGrid";
import BatchAnalysisProgress from "./BatchAnalysisProgress";
import AdDetailModal from "./AdDetailModal";
import BrandModal from "./BrandModal";
import ScrapeAllModal from "./ScrapeAllModal";
import type { SpyAd, SpyBrand, SpyTab } from "../types/adsSpy.types";
import { CreateAdWithIAModal } from "../../cretives/components/modals/CreateAdWithIAModal";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

const TAB_KEYS: { key: SpyTab; labelKey: string; icon: React.ReactNode }[] = [
  { key: "brands", labelKey: "spy-brands", icon: <Building2 className="w-4 h-4" /> },
  { key: "ads", labelKey: "spy-ads", icon: <LayoutGrid className="w-4 h-4" /> },
  { key: "bookmarks", labelKey: "spy-bookmarks", icon: <Star className="w-4 h-4" /> },
];

interface AdsSpyViewProps {
  onSwitchToImages?: () => void;
  /** When set, auto-opens the detail modal for this ad on first mount. */
  initialDetailAdId?: number | null;
}

export default function AdsSpyView({ onSwitchToImages, initialDetailAdId }: AdsSpyViewProps) {
  const { language } = useAuth();
  const lang = language as Language;
  const t = (key: string, fallback: string) => dictionary[key]?.[lang] ?? fallback;

  const {
    currentTab,
    setCurrentTab,
    brands,
    ads,
    adsLoading,
    hasMore,
    loadBrands,
    loadAds,
    loadMoreAds,
    loadAiFilters,
    toggleBookmark,
    bookmarks,
    loadBookmarks,
  } = useAdsSpyStore();

  // Modals
  const [detailAdId, setDetailAdId] = useState<number | null>(initialDetailAdId ?? null);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState<SpyBrand | null>(null);
  const [scrapeAllOpen, setScrapeAllOpen] = useState(false);
  const [spyAdReference, setSpyAdReference] = useState<SpyAd | null>(null);
  const [showAdStudio, setShowAdStudio] = useState(false);

  // Batch analysis
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [analyzeConfirmOpen, setAnalyzeConfirmOpen] = useState(false);
  const [pendingAnalyzeCount, setPendingAnalyzeCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState({
    visible: false,
    current: 0,
    total: 0,
    label: "",
  });

  // Initial load
  useEffect(() => {
    loadBrands();
    loadAiFilters();
    loadAds(true);
    loadBookmarks();
  }, []);

  const handleTabChange = (tab: SpyTab) => {
    setCurrentTab(tab);
    if (tab === "ads") loadAds(true);
    if (tab === "brands") loadBrands();
  };

  const handleOpenBrandModal = (brand?: SpyBrand) => {
    setEditingBrand(brand || null);
    setBrandModalOpen(true);
  };

  const handleViewBrandAds = (brandId: number) => {
    const { setFilter } = useAdsSpyStore.getState();
    setFilter("brand_id", String(brandId));
    setCurrentTab("ads");
    setTimeout(() => loadAds(true), 0);
  };

  const handleOpenAnalyzeConfirm = useCallback(async () => {
    setLoadingCount(true);
    try {
      const { filters } = useAdsSpyStore.getState();
      const params: Record<string, string | number> = {
        unanalyzed: "true",
        limit: 10,
        sort: "rank",
      };
      if (filters.brand_id) params.brand_id = filters.brand_id;

      const result = await adsSpyService.getAds(params);
      setPendingAnalyzeCount(result.data.length);
      setAnalyzeConfirmOpen(true);
    } catch {
      console.error("Error fetching pending ads");
    } finally {
      setLoadingCount(false);
    }
  }, []);

  const handleAnalyzeAll = useCallback(async () => {
    setAnalyzeConfirmOpen(false);
    setAnalyzingAll(true);
    try {
      const { filters } = useAdsSpyStore.getState();
      const params: Record<string, string | number> = {
        unanalyzed: "true",
        limit: 10,
        sort: "rank",
      };
      if (filters.brand_id) params.brand_id = filters.brand_id;

      const result = await adsSpyService.getAds(params);
      const pending = result.data;

      if (pending.length === 0) {
        setAnalyzingAll(false);
        return;
      }

      setAnalyzeProgress({
        visible: true,
        current: 0,
        total: pending.length,
        label: t("analyzing-n-ads", "Analyzing {n} ads...").replace("{n}", String(pending.length)),
      });

      let done = 0;
      for (const ad of pending) {
        try {
          await adsSpyService.analyzeAd(ad.id);
        } catch {
          // continue
        }
        done++;
        setAnalyzeProgress((p) => ({
          ...p,
          current: done,
          label:
            done === pending.length
              ? t("completed", "Completed")
              : t("analyzing-n-ads", "Analyzing {n} ads...").replace("{n}", String(pending.length)),
        }));
      }

      await loadAiFilters();
      loadAds(true);

      setTimeout(
        () => setAnalyzeProgress((p) => ({ ...p, visible: false })),
        4000
      );
    } catch {
      console.error("Error in batch analysis");
    } finally {
      setAnalyzingAll(false);
    }
  }, [loadAds, loadAiFilters]);

  // Stats
  const totalAds = brands.reduce((s, b) => s + (b.ad_count || 0), 0);

  const tabCounts: Record<SpyTab, number> = {
    brands: brands.length,
    ads: totalAds,
    bookmarks: bookmarks.length,
  };

  return (
    <div>
      {/* Sub-tabs + actions */}
      <div className="flex items-center justify-between mb-6 border-b border-gray-200">
        <div className="flex items-center gap-1">
          {TAB_KEYS.map((tab) => {
            const count = tabCounts[tab.key];
            const isActive = currentTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "text-fluxi"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab.icon}
                {t(tab.labelKey, tab.key)}
                {count > 0 && (
                  <span
                    className={`text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? "bg-[#EEF0FF] text-fluxi"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {count}
                  </span>
                )}
                {isActive && (
                  <span className="absolute bottom-0 left-2 right-2 h-[2px] rounded-full bg-fluxi" />
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 pb-1">
          {currentTab === "brands" && (
            <button
              onClick={() => handleOpenBrandModal()}
              className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full bg-fluxi text-white hover:bg-fluxi-dark transition-all duration-200 shadow-sm"
            >
              <Plus className="w-3.5 h-3.5" />
              {t("add-brand", "Add Brand")}
            </button>
          )}
          {currentTab === "ads" && brands.length > 0 && (
            <>
              <div className="relative group/analyze">
                <button
                  onClick={handleOpenAnalyzeConfirm}
                  disabled={analyzingAll || loadingCount}
                  className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full bg-[#EEF0FF] text-fluxi border border-[#D8D8F8] hover:bg-[#E0E3FF] transition-all duration-200 disabled:opacity-50"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {analyzingAll ? t("analyzing", "Analyzing...") : loadingCount ? t("loading", "Loading...") : t("analyze-all", "Analyze All")}
                </button>
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 text-[11px] font-medium text-white bg-gray-800 rounded-lg whitespace-nowrap opacity-0 group-hover/analyze:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                  {t("analyze-ads-with-ai-tooltip", "Analyze ads with AI to enable smart filters")}
                </span>
              </div>
              <button
                onClick={() => setScrapeAllOpen(true)}
                className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full bg-fluxi text-white hover:bg-fluxi-dark transition-all duration-200 shadow-sm"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {t("search-new-ads", "Search New Ads")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Ads tab */}
      {currentTab === "ads" && (
        <>
          <AdsSpyFilters />
          <BatchAnalysisProgress
            visible={analyzeProgress.visible}
            current={analyzeProgress.current}
            total={analyzeProgress.total}
            label={analyzeProgress.label}
          />
          <AdsSpyGrid
            ads={ads}
            loading={adsLoading}
            hasMore={hasMore}
            onLoadMore={loadMoreAds}
            onClickDetail={(id) => setDetailAdId(id)}
            onToggleBookmark={toggleBookmark}
            onCreateSimilar={(ad) => {
              setSpyAdReference(ad);
              setShowAdStudio(true);
            }}
            hasBrands={brands.length > 0}
            onAddBrand={() => handleOpenBrandModal()}
          />
        </>
      )}

      {/* Brands tab */}
      {currentTab === "brands" && (
        <BrandsTable
          onOpenBrandModal={handleOpenBrandModal}
          onViewBrandAds={handleViewBrandAds}
        />
      )}

      {/* Bookmarks tab */}
      {currentTab === "bookmarks" && (
        <BookmarksGrid
          onClickDetail={(id) => setDetailAdId(id)}
        />
      )}

      {/* Modals */}
      <AdDetailModal
        open={detailAdId !== null}
        onClose={() => setDetailAdId(null)}
        adId={detailAdId}
      />

      <BrandModal
        open={brandModalOpen}
        onClose={() => {
          setBrandModalOpen(false);
          setEditingBrand(null);
        }}
        brand={editingBrand}
      />

      <ScrapeAllModal
        open={scrapeAllOpen}
        onClose={() => setScrapeAllOpen(false)}
      />

      <CreateAdWithIAModal
        open={showAdStudio}
        onClose={() => {
          setShowAdStudio(false);
          setSpyAdReference(null);
        }}
        onGenerated={onSwitchToImages}
        initialReference={
          spyAdReference
            ? {
                image_url: spyAdReference.thumbnail_url || spyAdReference.image_url || "",
                headline: spyAdReference.headline || undefined,
                ad_copy: spyAdReference.ad_copy || undefined,
              }
            : undefined
        }
      />

      {/* Analyze confirm modal */}
      {analyzeConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setAnalyzeConfirmOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#EEF0FF] flex items-center justify-center mb-5">
                <Sparkles className="w-7 h-7 text-fluxi" />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
                {t("analyze-ads-with-ai", "Analyze ads with AI")}
              </h3>

              <p className="text-[15px] text-gray-700 leading-relaxed mb-2">
                {t("will-analyze-n-ads", "{n} ads will be analyzed that have not been processed yet.").replace("{n}", String(pendingAnalyzeCount))}
              </p>
              <p className="text-sm text-gray-500 leading-relaxed max-w-sm mb-8">
                {t("ai-will-classify-ads", "AI will classify each ad by asset type, visual format, messaging, hook, and offer. This will enable smart filters.")}
              </p>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setAnalyzeConfirmOpen(false)}
                  className="text-sm font-medium px-5 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  {t("cancel", "Cancel")}
                </button>
                <button
                  onClick={handleAnalyzeAll}
                  disabled={pendingAnalyzeCount === 0}
                  className="text-sm font-semibold px-6 py-2.5 rounded-lg bg-fluxi text-white hover:bg-[#4545D8] transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Sparkles className="w-4 h-4" />
                  {pendingAnalyzeCount === 0 ? t("no-pending-ads", "No pending ads") : t("analyze-n-ads", "Analyze {n} ads").replace("{n}", String(pendingAnalyzeCount))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
